import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appEnv } from '../../../app.config';

export type MessagingChannel = 'email' | 'sms' | 'chat';

export interface ProviderStatus {
  name: string;

  real: boolean;

  configured: boolean;

  connected: boolean;

  detail: string | null;
}

export interface MessagingLimits {

  dailyEmailCap: number;

  emailPerSendCap: number;

  emailSentToday: number;

  smsCostPerSegment: number;

  smsSegmentSize: number;

  smsSegmentCap: number;

  smsSentToday: number;

  allowedAttachmentExtensions: string[];

  defaultSmsSender: string;

  defaultEmailSender: string;
}

export type ConnectionStatus = Record<MessagingChannel, boolean>;

export interface ChannelHealth {
  lastCheckedAt: string | null;
  ok: boolean | null;
  message: string | null;
}

export type HealthStatus = Record<MessagingChannel, ChannelHealth>;

export interface SuppressedAddress {
  email: string;
  reason: 'bounced' | 'complained';
  bounce_count: number;
  last_event_at: string;
}

export interface MessagingSettings {
  email: ProviderStatus[];
  sms: ProviderStatus[];
  chat: ProviderStatus[];
  limits: MessagingLimits;
  connections: ConnectionStatus;
  health: HealthStatus;
  canManage: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MessagingSettingsService {
  constructor(private httpClient: HttpClient) {}

  get(): Observable<MessagingSettings> {
    return this.httpClient.get<MessagingSettings>(`${appEnv.api}crm/messaging/settings`);
  }

  update(limits: {
    dailyEmailCap: number;
    emailPerSendCap: number;
    smsCostPerSegment: number;
    allowedAttachmentExtensions: string;
    smsSegmentCap: number;
  }): Observable<MessagingLimits> {
    return this.httpClient.put<MessagingLimits>(`${appEnv.api}crm/messaging/settings`, {
      daily_email_cap: limits.dailyEmailCap,
      email_per_send_cap: limits.emailPerSendCap,
      sms_cost_per_segment: limits.smsCostPerSegment,
      allowed_attachment_extensions: limits.allowedAttachmentExtensions,
      sms_segment_cap: limits.smsSegmentCap
    });
  }

  updateConnection(channel: MessagingChannel, connected: boolean): Observable<{ connections: ConnectionStatus }> {
    return this.httpClient.put<{ connections: ConnectionStatus }>(`${appEnv.api}crm/messaging/settings/connections`, {
      channel,
      connected
    });
  }

  getSuppressed(): Observable<{ suppressed: SuppressedAddress[] }> {
    return this.httpClient.get<{ suppressed: SuppressedAddress[] }>(`${appEnv.api}crm/messaging/settings/suppressed`);
  }

  unsuppress(email: string): Observable<{ suppressed: SuppressedAddress[] }> {
    return this.httpClient.delete<{ suppressed: SuppressedAddress[] }>(
      `${appEnv.api}crm/messaging/settings/suppressed/${encodeURIComponent(email)}`
    );
  }

  checkConnections(force = false): Observable<{ connections: ConnectionStatus; health: HealthStatus }> {
    return this.httpClient.post<{ connections: ConnectionStatus; health: HealthStatus }>(
      `${appEnv.api}crm/messaging/settings/check-connections`,
      { force }
    );
  }
}
